import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, FormLabel, IconButton, Stack, Tooltip } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EditArtworkSection from 'commons/BoxSection';
import AudioPlayer from 'components/AudioPlayer';
import useUploadAudioS3 from 'hooks/useUploadAudioS3';

const MAX_SIZE_BYTES = 20 * 1024 * 1024; // 20MB

const EditArtworkAudio = ({ artworkFormState, isArtist }) => {
  const { t } = useTranslation();
  const { setValue, watch, register, formState } = artworkFormState;
  const { generateAudioKey, isAcceptedAudio } = useUploadAudioS3();

  const existingUrl = watch('audioUrl');
  const hasCertificate = formState?.defaultValues?.hasCertificate;

  const [fileSize, setFileSize] = useState(0);
  const [error, setError] = useState('');

  const canEdit = !!isArtist && !hasCertificate;

  const showUploadState = useMemo(() => {
    return !existingUrl;
  }, [existingUrl]);

  const onFileChange = async (e) => {
    setError('');
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!isAcceptedAudio(file) || file.size > MAX_SIZE_BYTES) {
      setError(t('please_upload_audio_mp3_wav_m4a_max_20mb'));
      return;
    }

    const key = generateAudioKey(file);
    const previewUrl = URL.createObjectURL(file);
    setFileSize(file.size);

    // Store pending state to be uploaded on Update action
    setValue('audioKey', key);
    setValue('audioRemoved', false);
    setValue('audioUrl', previewUrl);
    setValue('audioPendingFile', file);
    setValue('audioUploading', false);
    setValue('audioUploadProgress', 0);
  };

  const handleDelete = () => {
    if (!canEdit) return;
    setValue('audioRemoved', true);
    setValue('audioKey', null);
    setValue('audioUrl', null);
    setFileSize(0);
  };

  return (
    <EditArtworkSection
      sx={{ mt: 2 }}
      heading={showUploadState && t('audio_file')}
    >
      <Stack
        spacing={2}
        sx={{ mt: 2 }}
      >
        {showUploadState ? (
          <Box>
            <Button
              variant="contained"
              color="inherit"
              component="label"
              disabled={!canEdit}
              sx={{
                bgcolor: '#000',
                color: '#fff',
                '&:hover': { bgcolor: '#111' },
                px: 2.5,
              }}
            >
              {t('upload_audio')}
              <input
                hidden
                accept="audio/*"
                type="file"
                onChange={onFileChange}
              />
            </Button>
            {!!error && (
              <FormLabel
                error
                sx={{ mt: 1, display: 'block' }}
                ref={register('audio').ref}
              >
                {error}
              </FormLabel>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  fontFamily: 'Readex Pro',
                  fontWeight: 500,
                  fontSize: '20px',
                  lineHeight: '30px',
                  letterSpacing: '0.8px',
                  color: '#0F0F0F',
                }}
              >
                {t('audio_file')}
              </Box>
              <Tooltip title={t('delete_audio')}>
                <span>
                  <IconButton
                    onClick={handleDelete}
                    disabled={!canEdit}
                    sx={{ width: 24, height: 24, p: 0 }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 24 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <AudioPlayer
              audioUrl={existingUrl}
              fileSize={fileSize}
            />
          </Box>
        )}
      </Stack>
    </EditArtworkSection>
  );
};

export default EditArtworkAudio;
